import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import CandidateComplianceForm from "@/Components/candidate/CandidateComplianceForm";
import CandidatePaymentForm from "@/Components/candidate/CandidatePaymentForm";
import CandidatePersonalForm from "@/Components/candidate/CandidatePersonalForm";
import {
  handleCandidateDetailsSubmit,
  handleFetchCandidateDetails,
  handleFetchCandidateDocuments,
  handleUpdateCandidatePaymentDetails,
  toggleEditLoading,
} from "@/reducers/candidateCreate";
import { fetchCandidateDropdownData } from "@/reducers/clientcommon";
import useTitle from "@/utilities/hooks/useTitle";
import { apiDecrypt } from "@/utilities/keyEncryption";
import { getComplianceClass, showCommonToast } from "@/utilities/utils";
import { validateCandidatePersonalForm } from "@/validations/candidatePersonal";

const EditCandidate = () => {
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  console.log("Editing candidate with ID:", id);
  const candidateEditData = useSelector(
    (state) => state.candidateCreate.editData,
  );
  const editCandidateLoading = useSelector(
    (state) => state.candidateCreate.editLoading,
  );
  const clientUserAllPermissions = useSelector(
    (state) => state.session.clientUserAllPermissions,
  );
  const canViewCompliance =
    clientUserAllPermissions?.is_allow_to_upload_candidate_compliance !== 0;
  useTitle(`${candidateEditData.candidate_name} | Edit Candidate`);
  const dispatch = useDispatch();
  const [hasLoadedInitialCandidate, setHasLoadedInitialCandidate] =
    useState(false);

  useEffect(() => {
    setHasLoadedInitialCandidate(false);
    dispatch(toggleEditLoading(true));
    dispatch(fetchCandidateDropdownData());
    dispatch(
      handleFetchCandidateDetails({
        candidate_id: apiDecrypt(id),
      }),
    );
    dispatch(
      handleFetchCandidateDocuments({
        candidate_id: apiDecrypt(id),
      }),
    );
  }, [dispatch, id]);

  useEffect(() => {
    if (!editCandidateLoading) {
      setHasLoadedInitialCandidate(true);
    }
  }, [editCandidateLoading]);

  const tab = searchParams.get("tab");
  const section = searchParams.get("section");
  const from = searchParams.get("from");

  let defaultsShowForm = {
    personal: false,
    payment: false,
    compliance: false,
  };

  if (from === "propose" && tab === "compliance") {
    defaultsShowForm.compliance = true;
  } else {
    defaultsShowForm.personal = true;
  }

  const [showForm, setShowForm] = useState(defaultsShowForm);

  const [formSubmitted, setFormSubmitted] = useState({
    personal: false,
    payment: false,
    compliance: false,
  });
  const validHashForms = ["personal", "payment", "compliance"];

  // Show toast when redirected from propose
  useEffect(() => {
    if (from === "propose" && tab === "compliance") {
      const redirectContext = sessionStorage.getItem("proposeRedirect");
      if (redirectContext) {
        try {
          const context = JSON.parse(redirectContext);

          // Check if redirect is recent (within last 10 seconds)
          const isRecent = Date.now() - context.timestamp < 10000;

          if (isRecent && context.from === "propose_candidate") {
            // Show warning toast after a short delay
            setTimeout(() => {
              showCommonToast(
                "Please upload the required enhanced documents to propose this candidate.",
                {
                  type: "warning",
                  duration: 5000,
                },
              );
            }, 500);
          }
        } catch (e) {
          console.error("Error parsing redirect context:", e);
        }
      }
    }
  }, [from, tab]);

  useEffect(() => {
    // location.hash includes the leading '#'
    const hash = location.hash.slice(1);
    if (hash && validHashForms.includes(hash)) {
      setShowForm({
        personal: hash === "personal",
        payment: hash === "payment",
        compliance: hash === "compliance",
      });
    }
  }, [location]);

  // Check if candidate data exists OR if coming from propose
  const isFromPropose = from === "propose" && tab === "compliance";
  const shouldShowLoading =
    !isFromPropose && (!hasLoadedInitialCandidate || editCandidateLoading);
  const candidateNotFound =
    hasLoadedInitialCandidate &&
    !editCandidateLoading &&
    !isFromPropose &&
    Object.keys(candidateEditData).length === 0;

  if (candidateNotFound) {
    return <div>Candidate not found.</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="shadow-[0px_1px_8px_0px_#E7EFFB grid gap-3 rounded-[10px] bg-white py-6">
        <div className="flex justify-between px-5">
          <h1 className="text-[20px] font-semibold leading-[normal] text-black">
            Edit Candidate
          </h1>
          {candidateEditData.profile_percent !== undefined &&
            candidateEditData.profile_percent !== null && (
              <span
                className={`h-[28px] rounded-[4px] px-3 text-center text-[14px] font-medium leading-[26px] ${getComplianceClass(
                  candidateEditData.profile_percent,
                )}`}
              >
                {candidateEditData.profile_percent == "-"
                  ? "0%"
                  : candidateEditData.profile_percent + "%"}
              </span>
            )}
        </div>
        <div className="bg-lightblue px-5">
          <div>
            <button
              className={`inline-block border-b-2 px-4 py-2 ${showForm.personal ? "border-b-primary text-primary" : "border-b-lightblue text-txtDark"}`}
              onClick={() =>
                setShowForm({
                  personal: true,
                  payment: false,
                  compliance: false,
                })
              }
            >
              Personal
            </button>
            <button
              className={`inline-block border-b-2 px-4 py-2 ${showForm.payment ? "border-b-primary text-primary" : "border-b-lightblue text-txtDark"}`}
              onClick={() =>
                setShowForm({
                  personal: false,
                  payment: true,
                  compliance: false,
                })
              }
            >
              Payment
            </button>
            {canViewCompliance && (
              <button
                className={`inline-block border-b-2 px-4 py-2 ${showForm.compliance ? "border-b-primary text-primary" : "border-b-lightblue text-txtDark"}`}
                onClick={() =>
                  setShowForm({
                    personal: false,
                    payment: false,
                    compliance: true,
                  })
                }
              >
                Compliance
              </button>
            )}
          </div>
        </div>
        <div className="px-5">
          {/* CHANGED: Only show loading if NOT from propose */}
          {shouldShowLoading ? (
            <div>Loading...</div>
          ) : (
            <>
              {showForm.personal && (
                <Formik
                  initialValues={{
                    title: candidateEditData.candidate_title,
                    firstName: candidateEditData.candidate_forename,
                    lastName: candidateEditData.candidate_surname,
                    email: candidateEditData.candidate_email,
                    skills: candidateEditData.candidate_skill_ids
                      ? candidateEditData.candidate_skill_ids?.split(",")
                      : [],
                    mobile: candidateEditData.mobile_phone_number,
                    dateOfBirth: candidateEditData.candidate_dob,
                    gender: candidateEditData.candidate_gender,
                    setting: candidateEditData.candidate_parent_id,
                    role: candidateEditData.candidate_type_id,
                    postCode: candidateEditData.candidate_postcode,
                    address: candidateEditData.candidate_a1,
                    county: candidateEditData.county_id,
                    region: candidateEditData.region_id,
                    clientLat: candidateEditData.candidate_lat,
                    clientLng: candidateEditData.candidate_lng,
                    band: candidateEditData.band_id,
                    consultant: candidateEditData.compliance_consultant_id,
                    referral: candidateEditData.candidate_referer_id,
                    gmcNumber: candidateEditData.gmc_number,
                  }}
                  enableReinitialize={true}
                  validate={validateCandidatePersonalForm}
                  onSubmit={(values, { setSubmitting }) => {
                    const postData = {
                      ...values,
                      candidate_title: values.title,
                      candidate_forename: values.firstName,
                      candidate_surname: values.lastName,
                      candidate_gender: values.gender,
                      mobile_phone_number: values.mobile,
                      candidate_email: values.email,
                      candidate_parent_id: values.setting,
                      candidate_type_id: values.role,
                      candidate_a1: values.address,
                      county_id: values.county,
                      region_id: values.region,
                      candidate_postcode: values.postCode,
                      action: "edit",
                      candidate_lat: values.clientLat,
                      candidate_lng: values.clientLng,
                      candidate_dob: values.dateOfBirth,
                      band_id: values.band,
                      compliance_consultant_id: values.consultant,
                      candidate_referer_id: values.referral,
                      candidate_skill_ids: values.skills?.join(","),
                      candidate_id: apiDecrypt(id),
                      gmc_number: values.gmcNumber,
                    };
                    const callback = () => {
                      setSubmitting(false);
                      dispatch(
                        handleFetchCandidateDetails({
                          candidate_id: apiDecrypt(id),
                        }),
                      );
                      setShowForm({
                        personal: false,
                        payment: true,
                        compliance: false,
                      });
                    };
                    dispatch(
                      handleCandidateDetailsSubmit({ ...postData, callback }),
                    );
                  }}
                >
                  {() => (
                    <CandidatePersonalForm
                      fromEdit={true}
                      candidateData={candidateEditData}
                    />
                  )}
                </Formik>
              )}
              {showForm.compliance && (
                <CandidateComplianceForm
                  defaultSection={searchParams.get("section")}
                />
              )}
              {showForm.payment && (
                <Formik
                  initialValues={{
                    paymentMethod:
                      candidateEditData.candidate_payment_method || null,
                    umbrellaCompanyId:
                      candidateEditData.candidate_umbrella_company_id || null,
                    bdAccountName:
                      candidateEditData.candidate_bd_account_name || null,
                    bdAccountNo:
                      candidateEditData.candidate_bd_account_no || null,
                    bdSortCode: candidateEditData.candidate_bd_sortcode || null,
                  }}
                  enableReinitialize={false}
                  onSubmit={(values, { setSubmitting }) => {
                    const postData = {
                      candidate_id: apiDecrypt(id),
                      candidate_payment_method: values.paymentMethod,
                      candidate_umbrella_company_id: values.umbrellaCompanyId,
                      candidate_bd_account_name: values.bdAccountName,
                      candidate_bd_account_no: values.bdAccountNo,
                      candidate_bd_sortcode: values.bdSortCode,
                    };
                    const callback = () => {
                      dispatch(
                        handleFetchCandidateDetails({
                          candidate_id: apiDecrypt(id),
                        }),
                      );
                      setShowForm({
                        personal: false,
                        payment: false,
                        compliance: true,
                      });
                    };
                    dispatch(
                      handleUpdateCandidatePaymentDetails({
                        ...postData,
                        callback,
                        setSubmitting,
                      }),
                    );
                  }}
                >
                  {({
                    submitForm,
                    isSubmitting,
                    errors,
                    touched,
                    isValid,
                    setSubmitting,
                    validateForm,
                    values,
                    validateField,
                    setFieldValue,
                  }) => <CandidatePaymentForm id={apiDecrypt(id)} />}
                </Formik>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditCandidate;
